import React, { useContext, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import * as actionType from "../context/actions";
import DeleteExpenseView from "./DeleteExpenseView";
import PaginationView from "./PaginationView";
import { FilterdData } from "../helper/FilterdData";
import DataNotFoundView from "./DataNotFoundView";
import { Languages } from "../Utils/Languages";
import { Tooltip } from 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { resolveAuditName } from "../Utils/audit_helper";
import Currancy from "../Utils/currency.json";


const ExpenseTableDataLayer = ({ ExpenseData, selectedBusiness }) => {
    const { state, dispatch } = useContext(ConfigContext);
    const [filterdInvoiceData, setFilterdInvoiceData] = useState(ExpenseData);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // Enrich expense data for search
    const enrichExpenseData = (expenses) => {
        return expenses.map((expense, index) => {
            // Format issued date safely
            let issuedDate = "";
            try {
                const rawDate = expense?.expenseDate;
                if (rawDate) {
                    let ts = parseInt(rawDate);
                    if (!isNaN(ts)) {
                        if (ts.toString().length <= 10) ts *= 1000;
                        const d = new Date(ts);
                        if (!isNaN(d.getTime())) {
                            issuedDate = d.toDateString();
                        }
                    }
                }
            } catch (e) {
                issuedDate = "";
            }
            return {
                ...expense,
                srNo: index + 1,
                issuedDateFormatted: issuedDate,
            };
        });
    };

    // Filtered + enriched data
    const matchedExpenses = useMemo(() => {
        const filteredData = (selectedBusiness === null || selectedBusiness === "null")
            ? filterdInvoiceData
            : filterdInvoiceData.filter(expense => expense?.companyId == selectedBusiness);

        return enrichExpenseData(filteredData);
    }, [filterdInvoiceData, selectedBusiness]);

    useEffect(() => {
        setFilterdInvoiceData(FilterdData(state, ExpenseData, "createdDate"));
    }, [state]);

    const totalPages = Math.ceil(matchedExpenses.length / itemsPerPage);

    // Initialize tooltips
    useEffect(() => {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="DefaultTooltipThree"]');
        const tooltipList = Array.from(tooltipTriggerList).map(el => new Tooltip(el));
        return () => tooltipList.forEach(t => t.dispose());
    }, []);

    const sortCaret = (order) => (
        <span className="d-inline-flex align-items-center ms-2">
            {!order ? (
                <Icon icon="mdi:swap-vertical" className="text-muted" />
            ) : order === "asc" ? (
                <Icon icon="mdi:arrow-up-thick" className="text-muted" />
            ) : (
                <Icon icon="mdi:arrow-down-thick" className="text-muted" />
            )}
        </span>
    );

    const { SearchBar } = Search;

    const columns = [
        {
            dataField: "srNo",
            text: Languages[state?.LANGUAGE]?.s_r,
            align: "center",
            headerAlign: "center",
            sort: true,
            sortCaret,
        },
        {
            dataField: "expenseName",
            text: Languages[state?.LANGUAGE]?.Expense_Name,
            sort: true,
            style: { minWidth: "150px" },
            sortCaret,
            formatter: (cell, row) => {
                const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(row.companyId));
                const isShared = business?.isShared === true;

                const currentUserStr = state?.SET_USER?.name || state?.SET_USER?.email || state?.SET_USER?.contact || "guest";
                const resolvedName = resolveAuditName(row.createdBy, state.membersData);
                const creatorName = (resolvedName === "Unknown" && business?.ownerName)
                    ? business.ownerName
                    : resolvedName;
                const isCreator = creatorName === currentUserStr || row.createdBy === currentUserStr || (typeof row.createdBy === 'object' && row.createdBy?.uid === state?.USERID);

                const showByLabel = row.createdBy
                    ? !isCreator
                    : (isShared && business?.ownerName);

                const displayName = row.createdBy ? creatorName : business?.ownerName;

                return (
                    <div style={{ marginLeft: -8, cursor: "pointer" }}>
                        <div>{cell}</div>
                        {showByLabel && (
                            <small className="text-secondary-light" style={{ fontSize: '11px', display: 'block', marginTop: '-4px' }}>
                                by: {displayName}
                            </small>
                        )}
                    </div>
                );
            },
        },
        {
            dataField: "amount",
            text: Languages[state?.LANGUAGE]?.amount,
            sort: true,
            sortCaret,
            formatter: (cell, row) => {
                const matchedCurrency = row?.currency ? Currancy.CountryCurrencyResponse.find(
                    (c) => c.currencyCode === row.currency
                ) : null;
                const currencyCode = matchedCurrency ? matchedCurrency.currencyCode : state?.CURRENCYCODE;
                const currencySymbol = matchedCurrency ? matchedCurrency.currencytSymbol : state?.SYMBOL;
                return (
                    <span>{currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `} {cell}</span>
                );
            },
        },
        {
            dataField: "paymentMethod",
            text: Languages[state?.LANGUAGE]?.Payment_Method,
            align: "center",
            headerAlign: "center",
            sort: true,
            sortCaret,
            formatter: (cell) => (
                <span className="d-inline-block text-center px-3 py-1 rounded-pill fw-medium text-sm bg-primary-light text-primary-600">
                    {cell || ""}
                </span>
            ),
        },
        {
            dataField: "issuedDateFormatted",
            text: Languages[state?.LANGUAGE]?.issued_date,
            sort: true,
            sortCaret,
        },
        {
            dataField: "action",
            text: Languages[state?.LANGUAGE]?.action,
            align: "center",
            headerAlign: "center",
            searchable: false,
            formatter: (cell, row) => (
                <>
                    <Link
                        onClick={(e) => {
                            if (!state?.USERID) {
                                e.preventDefault();
                                dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                return;
                            }
                            dispatch({ type: actionType.SELECTEDEXPENSE, payload: row });
                        }}
                        to="/expense-view"
                        className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-primary"
                        data-bs-title="View Expense"
                    >
                        <Icon icon="iconamoon:eye-light" />
                    </Link>
                    <Link
                        onClick={(e) => {
                            if (!state?.USERID) {
                                e.preventDefault();
                                dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                return;
                            }
                            dispatch({ type: actionType.SELECTEDEXPENSE, payload: row?.expenseId });
                        }}
                        to="/expense-edit"
                        className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-success"
                        data-bs-title="Edit Expense"
                    >
                        <Icon icon="lucide:edit" />
                    </Link>
                    <Link
                        data-bs-toggle="DefaultTooltipThree"
                        data-bs-placement="top"
                        data-bs-custom-class="tooltip-primary"
                        data-bs-title="Delete Expense"
                    >
                        <DeleteExpenseView FirebaseId={row?.uniqueID} />
                    </Link>
                </>
            ),
        },
    ];

    const handleSelection = (val) => {
        setItemsPerPage(val);
        setCurrentPage(1);
    };

    return (
        <div className="card">
            <div className="card-body">
                <ToolkitProvider keyField="expenseId" data={matchedExpenses} columns={columns} search>
                    {props => {
                        const searchText = props.searchProps.searchText?.toLowerCase() || "";

                        const filteredData = matchedExpenses.filter(exp => {
                            return (
                                exp.expenseName?.toLowerCase().includes(searchText) ||
                                exp.amount?.toString().toLowerCase().includes(searchText) ||
                                exp.paymentMethod?.toLowerCase().includes(searchText) ||
                                exp.issuedDateFormatted?.toLowerCase().includes(searchText)
                            );
                        });

                        const start = (currentPage - 1) * itemsPerPage;
                        const paginatedSearchData = filteredData.slice(start, start + itemsPerPage);

                        return (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <SearchBar
                                        {...props.searchProps}
                                        placeholder={Languages[state?.LANGUAGE]?.Search_expenses}
                                        className="form-control w-auto"
                                    />
                                </div>
                                <div className="table-responsive">
                                    <BootstrapTable
                                        {...props.baseProps}
                                        bootstrap4
                                        bordered={false}
                                        data={paginatedSearchData}
                                        wrapperClasses={`custom-table-border invoice-table ${localStorage.getItem("theme") === "dark" ? "table-dark" : "table-light"}`}
                                        defaultSorted={{ dataField: "date", order: "desc" }}
                                        noDataIndication={() => <DataNotFoundView height={400} />}
                                        rowStyle={{ cursor: "pointer" }}
                                    />
                                </div>

                                {filteredData.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="d-flex align-items-center gap-2 entry_per_page">
                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon"
                                                    type="button"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    {itemsPerPage}
                                                </button>
                                                <ul className="dropdown-menu shadow-md">
                                                    {[5, 10, 15, 20].map((value) => (
                                                        <li key={value}>
                                                            <Link
                                                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${itemsPerPage == value && "bg-primary-50"
                                                                    }`}
                                                                onClick={() => handleSelection(value)}
                                                            >
                                                                {value}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <span className="text-secondary-light">{Languages[state?.LANGUAGE]?.entries_per_page}</span>
                                        </div>

                                        <div className="ms-auto">
                                            <PaginationView
                                                currentPage={currentPage}
                                                totalPages={Math.ceil(filteredData.length / itemsPerPage)}
                                                handlePageChange={setCurrentPage}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    }}
                </ToolkitProvider>
            </div>
        </div>
    );
};

export default ExpenseTableDataLayer;
